import { create } from 'zustand';
import type { UserListQuery, UserRole, UserStatus } from '../types';

interface FiltersState extends UserListQuery {
  selectedIds: string[];
  setFilter: <K extends keyof UserListQuery>(key: K, value: UserListQuery[K]) => void;
  setPage: (page: number) => void;
  toggleSelected: (id: string) => void;
  setSelected: (ids: string[]) => void;
  clearSelected: () => void;
  reset: () => void;
}

const INITIAL: UserListQuery = {
  page: 1,
  limit: 20,
  sortBy: 'createdAt',
  order: 'desc',
};

export const useUserFilters = create<FiltersState>((set) => ({
  ...INITIAL,
  selectedIds: [],
  setFilter: (key, value) =>
    set((s) => ({ ...s, [key]: value, page: key === 'page' ? (value as number) : 1 })),
  setPage: (page) => set({ page }),
  toggleSelected: (id) =>
    set((s) => ({
      selectedIds: s.selectedIds.includes(id)
        ? s.selectedIds.filter((x) => x !== id)
        : [...s.selectedIds, id],
    })),
  setSelected: (ids) => set({ selectedIds: ids }),
  clearSelected: () => set({ selectedIds: [] }),
  reset: () => set({ ...INITIAL, selectedIds: [] }),
}));

const QUERY_KEYS: (keyof UserListQuery)[] = [
  'page',
  'limit',
  'search',
  'status',
  'role',
  'plan',
  'country',
  'isVerified',
  'joinedAfter',
  'joinedBefore',
  'sortBy',
  'order',
];

export function selectUserQuery(s: FiltersState): UserListQuery {
  const out: UserListQuery = {};
  for (const k of QUERY_KEYS) {
    const v = s[k];
    if (v !== undefined && v !== '') (out as Record<string, unknown>)[k] = v;
  }
  return out;
}

export const STATUS_OPTIONS: UserStatus[] = ['ACTIVE', 'SUSPENDED', 'DELETED'];
export const ROLE_OPTIONS: UserRole[] = ['USER', 'EDITOR', 'ADMIN', 'SUPER_ADMIN'];
